import { createTheme } from '@mui/material/styles';
import { tokens } from './tokens.generated';

/**
 * THE product theme. Every value derives from tokens.generated.ts (itself
 * generated from tokens/thesis3.tokens.json) — never hand-copy a hex here.
 *
 * Brand mapping:
 *  - primary   = ink  (chrome: app bar, outlined buttons, structure)
 *  - secondary = pink (the brand's primary ACTION color: contained buttons)
 *  - focus ring = teal, display rule = gold (used by components, not palette)
 */
const { color, font, radius, shadow } = tokens;

const displayHeading = {
  fontFamily: font.display,
  fontWeight: 900,
  letterSpacing: '0.02em',
};

/** Uppercase display style for h1–h4 per the brand's display convention. */
const displayHeadingUpper = {
  ...displayHeading,
  textTransform: 'uppercase' as const,
};

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: color.ink,
      light: color.inkHover,
      dark: color.ink,
      contrastText: color.cream,
    },
    secondary: {
      main: color.pink,
      dark: color.pinkHover,
      contrastText: color.white,
    },
    error: { main: color.danger },
    warning: { main: color.warning },
    success: { main: color.success },
    info: { main: color.teal },
    background: {
      default: color.background,
      paper: color.surface,
    },
    text: {
      primary: color.text,
      secondary: color.textMuted,
    },
    divider: color.border,
  },
  typography: {
    fontFamily: font.body,
    h1: { ...displayHeadingUpper, fontSize: '2.5rem' },
    h2: { ...displayHeadingUpper, fontSize: '2rem' },
    h3: { ...displayHeadingUpper, fontSize: '1.6rem' },
    h4: { ...displayHeadingUpper, fontSize: '1.3rem' },
    h5: { ...displayHeading, fontSize: '1.1rem' },
    h6: { ...displayHeading, fontSize: '1rem' },
    button: { fontWeight: 700, textTransform: 'none' },
  },
  shape: {
    borderRadius: radius.base,
  },
  components: {
    MuiButton: {
      defaultProps: {
        // Pink is the brand's action color: plain <Button variant="contained">
        // is a pink action button unless a caller opts into another color.
        color: 'secondary',
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          fontWeight: 700,
          borderRadius: radius.base,
        },
        containedSecondary: {
          '&:hover': { backgroundColor: color.pinkHover },
          '&:active': { backgroundColor: color.pinkActive },
        },
        // Outlined buttons are ink chrome, whatever palette color was passed.
        outlined: {
          color: color.ink,
          borderColor: color.ink,
          '&:hover': {
            color: color.inkHover,
            borderColor: color.inkHover,
            backgroundColor: color.surface,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radius.large,
          border: `1px solid ${color.border}`,
          boxShadow: shadow.card,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          // Teal focus ring on every outlined input (TextField default variant).
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: color.teal,
            borderWidth: 2,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          borderRadius: radius.pill,
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: color.ink,
          color: color.cream,
        },
      },
    },
  },
});

export type Theme = typeof theme;
